import type React from "react";
import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { List, IconButton, Collapse, Box, useTheme } from "@mui/material";
import {
  KeyboardArrowDown as ArrowDownIcon,
  Payments as PaymentsIcon,
  ListAlt as OrdersIcon,
  AssignmentLate as ActionRequiredIcon,
  List as AllIcon,
} from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type translations from "../../translations/translations.json";
import SidebarMenuItem from "../SidebarMenuItem";
import { rawColor } from "../../constants/colors";
import { TestId } from "../../constants/testIds";

type TranslationKeys = keyof typeof translations.en;

interface UangJalanMenuProps {
  onNavigate?: () => void;
}

const UangJalanMenu: React.FC<UangJalanMenuProps> = ({ onNavigate }) => {
  const location = useLocation();
  const { t } = useTranslation();
  const theme = useTheme();
  const ordersManuallyCollapsedRef = useRef(false);
  const ordersManuallyExpandedRef = useRef(false);
  const reportsManuallyCollapsedRef = useRef(false);
  const reportsManuallyExpandedRef = useRef(false);

  const isOrdersPage = useMemo(() => {
    return (
      location.pathname === "/orders" ||
      location.pathname === "/orders/action-required" ||
      location.pathname.startsWith("/orders/")
    );
  }, [location.pathname]);

  const isOrdersListPage = useMemo(() => {
    return location.pathname === "/orders" || location.pathname === "/orders/action-required";
  }, [location.pathname]);

  const isReportsPage = useMemo(() => {
    return (
      location.pathname === "/uang-jalan-reports" ||
      location.pathname === "/uang-jalan-reports/action-required" ||
      location.pathname.startsWith("/uang-jalan-reports/")
    );
  }, [location.pathname]);

  const isReportsListPage = useMemo(() => {
    return (
      location.pathname === "/uang-jalan-reports" ||
      location.pathname === "/uang-jalan-reports/action-required"
    );
  }, [location.pathname]);

  const [ordersExpanded, setOrdersExpanded] = useState(() => isOrdersListPage);
  const [reportsExpanded, setReportsExpanded] = useState(() => isReportsListPage);

  useEffect(() => {
    if (isOrdersListPage) {
      if (ordersManuallyExpandedRef.current) {
        setOrdersExpanded(true);
      } else if (ordersManuallyCollapsedRef.current) {
        setOrdersExpanded(false);
      } else {
        setOrdersExpanded(true);
      }
    } else if (!isOrdersPage) {
      setOrdersExpanded(false);
      ordersManuallyExpandedRef.current = false;
      ordersManuallyCollapsedRef.current = false;
    }
  }, [isOrdersListPage, isOrdersPage]);

  useEffect(() => {
    if (isReportsListPage) {
      if (reportsManuallyExpandedRef.current) {
        setReportsExpanded(true);
      } else if (reportsManuallyCollapsedRef.current) {
        setReportsExpanded(false);
      } else {
        setReportsExpanded(true);
      }
    } else if (!isReportsPage) {
      setReportsExpanded(false);
      reportsManuallyExpandedRef.current = false;
      reportsManuallyCollapsedRef.current = false;
    }
  }, [isReportsListPage, isReportsPage]);

  const toggleOrders = useCallback(() => {
    setOrdersExpanded((prev) => {
      const next = !prev;
      if (next) {
        ordersManuallyExpandedRef.current = true;
        ordersManuallyCollapsedRef.current = false;
      } else {
        ordersManuallyCollapsedRef.current = true;
        ordersManuallyExpandedRef.current = false;
      }
      return next;
    });
  }, []);

  const toggleReports = useCallback(() => {
    setReportsExpanded((prev) => {
      const next = !prev;
      if (next) {
        reportsManuallyExpandedRef.current = true;
        reportsManuallyCollapsedRef.current = false;
      } else {
        reportsManuallyCollapsedRef.current = true;
        reportsManuallyExpandedRef.current = false;
      }
      return next;
    });
  }, []);

  const submenuItemSx = { pl: 4, py: 0.375, minHeight: "unset" } as const;

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            position: "relative",
          }}
        >
          <SidebarMenuItem
            to="/orders/action-required"
            selected={isOrdersListPage}
            icon={<OrdersIcon />}
            text={t("Orders" as TranslationKeys)}
            sx={{ flex: 1 }}
            onNavigate={onNavigate}
            data-testid={TestId.SIDEBAR.MENU_ITEM_ORDERS}
          />
          <IconButton
            size="small"
            onClick={toggleOrders}
            sx={{
              transform: ordersExpanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: theme.transitions.create("transform", {
                duration: theme.transitions.duration.shorter,
              }),
              position: "absolute",
              right: 4,
              zIndex: 1,
              color: rawColor.white.transparent["07"],
              "&:hover": {
                backgroundColor: rawColor.white.transparent["12"],
              },
            }}
          >
            <ArrowDownIcon />
          </IconButton>
        </Box>
        <Collapse in={ordersExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding data-testid={TestId.SIDEBAR.ORDERS_MENU}>
            <SidebarMenuItem
              to="/orders/action-required"
              selected={location.pathname === "/orders/action-required"}
              icon={<ActionRequiredIcon fontSize="small" />}
              text={t("sidebar.actionRequired" as TranslationKeys)}
              isSubmenu
              textProps={{ fontSize: "0.875rem" }}
              sx={submenuItemSx}
              onNavigate={onNavigate}
              data-testid={TestId.SIDEBAR.MENU_ITEM_ORDERS_ACTION_REQUIRED}
            />
            <SidebarMenuItem
              to="/orders"
              selected={location.pathname === "/orders"}
              icon={<AllIcon fontSize="small" />}
              text={t("sidebar.all" as TranslationKeys)}
              isSubmenu
              textProps={{ fontSize: "0.875rem" }}
              sx={submenuItemSx}
              onNavigate={onNavigate}
              data-testid={TestId.SIDEBAR.MENU_ITEM_ORDERS_ALL}
            />
          </List>
        </Collapse>
      </Box>

      <Box sx={{ width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            position: "relative",
          }}
        >
          <SidebarMenuItem
            to="/uang-jalan-reports/action-required"
            selected={isReportsListPage}
            icon={<PaymentsIcon />}
            text={t("Uang Jalan Report" as TranslationKeys)}
            sx={{ flex: 1 }}
            onNavigate={onNavigate}
            data-testid={TestId.SIDEBAR.MENU_ITEM_UANG_JALAN}
          />
          <IconButton
            size="small"
            onClick={toggleReports}
            sx={{
              transform: reportsExpanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: theme.transitions.create("transform", {
                duration: theme.transitions.duration.shorter,
              }),
              position: "absolute",
              right: 4,
              zIndex: 1,
              color: rawColor.white.transparent["07"],
              "&:hover": {
                backgroundColor: rawColor.white.transparent["12"],
              },
            }}
          >
            <ArrowDownIcon />
          </IconButton>
        </Box>
        <Collapse in={reportsExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding data-testid={TestId.SIDEBAR.UANG_JALAN_MENU}>
            <SidebarMenuItem
              to="/uang-jalan-reports/action-required"
              selected={location.pathname === "/uang-jalan-reports/action-required"}
              icon={<ActionRequiredIcon fontSize="small" />}
              text={t("sidebar.actionRequired" as TranslationKeys)}
              isSubmenu
              textProps={{ fontSize: "0.875rem" }}
              sx={submenuItemSx}
              onNavigate={onNavigate}
              data-testid={TestId.SIDEBAR.MENU_ITEM_UJR_ACTION_REQUIRED}
            />
            <SidebarMenuItem
              to="/uang-jalan-reports"
              selected={location.pathname === "/uang-jalan-reports"}
              icon={<AllIcon fontSize="small" />}
              text={t("sidebar.all" as TranslationKeys)}
              isSubmenu
              textProps={{ fontSize: "0.875rem" }}
              sx={submenuItemSx}
              onNavigate={onNavigate}
              data-testid={TestId.SIDEBAR.MENU_ITEM_UJR_ALL}
            />
          </List>
        </Collapse>
      </Box>
    </>
  );
};

export default UangJalanMenu;
